import type { Machine } from './BackendDataService';
import { orgNatsClient, StatementIds } from './OrgNATSClient';
import { isDemoMode } from './demoMode';

/**
 * Persisted layout for a single machine in a line flow.
 */
export interface MachineFlowEntry {
  machineId: string;   // maschinennummer or maschineBK
  flowOrder: number;   // Position in the chain (0-based)
  flowGroup: number;   // Group index — same group = parallel machines
}

/**
 * Apply the line's machine flow order.
 *
 * SAP is the single source of truth. The order comes from SAP's
 * `orderNum`/`orderLine` (delivered as camelCase; the snake_case
 * `order_num`/`order_line` and the pre-mapped `flowOrder`/`flowGroup` are kept
 * only as fallbacks). There is no localStorage overlay — a saved order is
 * persisted to the backend (see {@link saveFlowLayoutToBackend}) and becomes
 * visible to every user on the next SAP refresh.
 *
 * The third parameter is accepted for backwards compatibility and ignored.
 */
export function applyFlowLayout(
  _lineId: string,
  machines: Machine[],
  _sapLastFetched?: Date | string | null,
): Machine[] {
  return machines.map((m, i) => ({
    ...m,
    flowOrder:
      m.flowOrder ??
      (m as any).orderNum ??
      (m as any).order_num ??
      i,
    flowGroup:
      m.flowGroup ??
      (m as any).orderLine ??
      (m as any).order_line ??
      i,
  })).sort((a, b) => (a.flowOrder ?? 0) - (b.flowOrder ?? 0));
}

/**
 * Build MachineFlowEntry array from current machines (for saving).
 */
export function buildFlowEntries(machines: Machine[]): MachineFlowEntry[] {
  return machines.map((m, i) => ({
    machineId: m.maschinennummer || m.maschineBK,
    flowOrder: m.flowOrder ?? i,
    flowGroup: m.flowGroup ?? i,
  }));
}

/**
 * Save flow layout to backend via NATS.
 * Sends each machine's order_num and order_line to be persisted in the database.
 * This is the ONLY persistence path — once saved, SAP returns the order to all
 * users on the next refresh.
 */
export async function saveFlowLayoutToBackend(
  lineId: string,
  machines: MachineFlowEntry[],
  token: string
): Promise<boolean> {
  // A saved layout becomes global for every user on the next SAP refresh, so
  // demo mode must never write one. Reading (`applyFlowLayout`) stays enabled —
  // otherwise every line chain would lose its ordering.
  if (isDemoMode()) {
    console.warn('FlowLayout: layout editing is disabled in demo mode');
    return false;
  }

  try {
    const response = await orgNatsClient.request(
      StatementIds.SAVE_FLOW_LAYOUT,
      {
        lineId,
        machines: machines.map(m => ({
          maschinennummer: m.machineId,
          order_num: m.flowOrder,
          order_line: m.flowGroup,
        })),
      },
      token,
      { timeout: 10000, service: 'cockpitservice' }
    );

    if (response.success) {
      console.log('✅ FlowLayout: Saved to backend for line', lineId);
      return true;
    } else {
      console.error('❌ FlowLayout: Backend save failed:', response.error);
      return false;
    }
  } catch (error) {
    console.error('❌ FlowLayout: NATS request failed:', error);
    return false;
  }
}
